/**
 * EmailUnreadContext
 * App-wide source of the Gmail inbox unread count so the sidebar's Email badge
 * is live from first load — the way MessagingContext keeps the Messages badge
 * live. EmailView only broadcasts counts while it is open, which left the
 * top-level Email item with no badge until you opened the inbox.
 *
 * The Gmail `/labels` list endpoint omits per-label counts, so the inbox count
 * comes from the `/labels/INBOX` detail endpoint (messagesUnread). That count is
 * authoritative for both the top-level Email badge and the Inbox sub-tab; the
 * other sub-folders keep their best-effort counts from EmailView's broadcast.
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../../platform/auth/AuthContext';
import { getHeader, parseAddress } from '../../services/gmailService';
import { MailService } from '../../services/mail/mailService';
import { isEventForProvider, resolveMailProvider, useMailProvider } from '../../services/mail/mailProvider';
import { DataService } from '../../services/DataService';
import { createNotification } from '../../utils/notificationHelpers';
import { supabase } from '../../utils/supabase/client';
import { isInternalRole } from '../../utils/permissions';
import { useAdaptivePoll } from '../../hooks/useAdaptivePoll';
import { createTabShare, type TabShare } from '../../polling/tabShare';
import type { BackoffPolicy, PollResult } from '../../polling/adaptivePoller';

interface EmailUnreadContextType {
  unreadByLabel: Record<string, number>;
  inboxUnread: number;
  refresh: () => Promise<void>;
}

const EmailUnreadContext = createContext<EmailUnreadContextType | undefined>(undefined);

/** Gmail returns `snippet` HTML-escaped, so a subject line with an ampersand
 *  or a quote arrives as `&amp;` / `&#39;` and would render literally in the
 *  notification row. Only the handful Gmail actually emits are decoded — this
 *  is a preview string, not a place to parse HTML. */
function decodeSnippet(raw: string): string {
  return raw
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    // Decode &amp; last so a genuinely double-escaped ampersand (e.g. a
    // literal "&lt;" that arrived as "&amp;lt;") can't chain into another
    // round of decoding above and turn back into a live '<'.
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

type InboxPoll = { inboxUnread: number; raised: number };

const POLL_POLICY: BackoffPolicy = { baseMs: 2 * 60_000, maxMs: 10 * 60_000 };
// With Gmail push live the server raises the email alert and broadcasts the
// Inbox count itself, so this poll is only the safety net for a lapsed watch.
const PUSH_POLL_POLICY: BackoffPolicy = { baseMs: 10 * 60_000, maxMs: 30 * 60_000 };
// A return to the app inside this long of the last read, in any tab, reuses it.
// With push live the count is already current on return, so only a long
// absence earns a read.
const RETURN_MIN_GAP_MS = 60_000;
const LIFECYCLE_SYNC_MS = 15 * 60_000;
const LIFECYCLE_POLICY: BackoffPolicy = { baseMs: LIFECYCLE_SYNC_MS, maxMs: LIFECYCLE_SYNC_MS };
const FAILURE_PAUSE_MS = 10 * 60_000;

/**
 * POST /watch answers a healthy watch with `skipped: 'already_watching'`, the
 * steady state, and only `pubsub_not_configured` means there is no push.
 */
export function isWatchLive(response: { success?: boolean; skipped?: string }): boolean {
  return response.success !== false && response.skipped !== 'pubsub_not_configured';
}

// New-email -> notification (per user, private). The poll runs in the signed-in
// user's own session against THEIR Gmail token, so every notification it creates
// is for that user only (RLS keeps it private). A persisted "already notified"
// set per user prevents re-alerting the same message every poll, and the first
// run baselines the current unread mail so we never flood on initial sync.
const NOTIFIED_KEY = (userId: string) => `quantis_email_notified_${userId}`;
// Baseline timestamp: messages older than this device's first-seen time are
// skipped so rotating top-25 windows on a busy mailbox don't fire stale alerts.
const BASELINE_TS_KEY = (userId: string) => `quantis_email_baseline_ts_${userId}`;
const MAX_NOTIFY_PER_POLL = 5;   // cap a burst so a backlog doesn't flood Alerts
const MAX_TRACKED_IDS = 250;     // bound localStorage growth

function loadNotifiedIds(userId: string): Set<string> | null {
  try {
    const raw = localStorage.getItem(NOTIFIED_KEY(userId));
    if (raw == null) return null; // null = no baseline yet -> baseline, don't notify
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return null;
  }
}

function saveNotifiedIds(userId: string, ids: Set<string>): void {
  try {
    const arr = Array.from(ids).slice(-MAX_TRACKED_IDS);
    localStorage.setItem(NOTIFIED_KEY(userId), JSON.stringify(arr));
  } catch {
    /* storage full / disabled — non-fatal */
  }
}

function loadBaselineTs(userId: string): number | null {
  try {
    const raw = localStorage.getItem(BASELINE_TS_KEY(userId));
    if (raw == null) return null;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

function saveBaselineTs(userId: string, ts: number): void {
  try {
    localStorage.setItem(BASELINE_TS_KEY(userId), String(ts));
  } catch {
    /* storage full / disabled — non-fatal */
  }
}

export function EmailUnreadProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  // Authoritative inbox unread (from /labels/INBOX detail).
  const [inboxUnread, setInboxUnread] = useState(0);
  const inboxRef = useRef<number | null>(null);
  // Best-effort per-folder counts from EmailView's broadcast (sub-tabs only).
  const [broadcastByLabel, setBroadcastByLabel] = useState<Record<string, number>>({});
  // A failed Gmail read (not connected, token revoked) parks polling for a
  // while instead of retrying on every interval and every return to the tab.
  const disabledUntilRef = useRef(0);
  // Re-entrancy guard: the poll interval and focus/online listeners can overlap;
  // without this, two concurrent runs read the same notified set before either
  // writes, causing duplicate notifications. Cross-device dedup needs a server
  // messageId guard (out of scope for this client-side file).
  const checkingRef = useRef(false);
  // Push is live only when both halves are: this tab's broadcast channel is
  // joined and the server confirmed a Gmail watch.
  const [channelLive, setChannelLive] = useState(false);
  const [watchLive, setWatchLive] = useState(false);
  const sharesRef = useRef<{ inbox: TabShare<InboxPoll>; lifecycle: TabShare<boolean> } | null>(null);
  const [started, setStarted] = useState(false);

  // Every call this context makes is server-gated to internal
  // (super_admin/internal/staff) sessions only — gmail_proxy_routes.tsx's
  // isCallerInternal() rejects everyone else with a 403. Without this check
  // every customer-side session polled and 403'd forever, since the
  // disable-on-error path only takes effect after the failing round trip.
  const eligible = Boolean(user?.id) && isInternalRole(user?.role) && !DataService.isDemoMode();
  // Outlook replaces Gmail here once linked. Its new-mail alerts are raised
  // by the server's Graph sync, so the client-side alert pass is Gmail-only.
  const { provider, resolved: providerResolved, userId: providerUserId } = useMailProvider();
  const providerReady = providerResolved && providerUserId === user?.id;
  useEffect(() => {
    if (eligible && user?.id) void resolveMailProvider(user.id);
  }, [eligible, user?.id]);

  const applyInbox = useCallback((count: number) => {
    inboxRef.current = count;
    setInboxUnread(count);
  }, []);

  const readInboxCount = useCallback(async (): Promise<number> => {
    try {
      const r = await MailService.label('INBOX');
      return r.label?.messagesUnread ?? 0;
    } catch (error) {
      disabledUntilRef.current = Date.now() + FAILURE_PAUSE_MS;
      throw error;
    }
  }, []);

  const fetchInbox = useCallback(async () => {
    if (!eligible) return;
    try {
      applyInbox(await readInboxCount());
    } catch {
      /* readInboxCount has parked polling */
    }
  }, [eligible, applyInbox, readInboxCount]);

  // Detect newly-arrived inbox mail and raise a private notification for THIS
  // user, returning how many were raised. Runs on the same cadence as the
  // unread-count poll.
  const checkNewEmails = useCallback(async (): Promise<number> => {
    const userId = user?.id;
    if (!eligible || !userId || provider !== 'gmail') return 0;

    // BUG 4 fix: prevent concurrent runs (interval + focus/online can overlap).
    if (checkingRef.current) return 0;
    checkingRef.current = true;
    let raised = 0;

    try {
      const res = await MailService.listMessages({ label: 'INBOX', q: 'is:unread', maxResults: 25 });
      const ids = (res.messages ?? []).map((m) => m.id);

      const notified = loadNotifiedIds(userId);
      const baselineTs = loadBaselineTs(userId);
      if (notified === null || baselineTs === null) {
        // Baseline branch, taken on a user's first run AND on upgrade from before
        // the recency guard shipped (a notified set exists but no timestamp).
        // Fold the current unread into the notified set and stamp the watermark,
        // so we notify nothing now and only mail arriving AFTER this point alerts.
        // Without the baselineTs===null arm, existing users would never get a
        // watermark and a busy inbox's top-25 rotation would fire stale alerts.
        const baselined = new Set<string>(notified ?? []);
        ids.forEach((id) => baselined.add(id));
        saveNotifiedIds(userId, baselined);
        saveBaselineTs(userId, Date.now());
        return 0;
      }
      if (ids.length === 0) return 0;

      const fresh = ids.filter((id) => !notified.has(id));
      if (fresh.length === 0) return 0;

      // BUG 1 + 2 fix: do NOT mark ids notified up-front. Only mark each id
      // after its notification is successfully created so a transient failure
      // doesn't permanently drop the alert. Emails beyond the per-poll cap
      // stay un-notified and are picked up on the next poll.
      const toNotify = fresh.slice(0, MAX_NOTIFY_PER_POLL);
      const meta = await MailService.batchMetadata(toNotify);

      // Index by id so we can iterate toNotify in order and handle missing
      // messages gracefully (missing = skip this poll, retry next time).
      const msgById = new Map(
        (meta.messages ?? []).map((m) => [m.id, m]),
      );

      for (const id of toNotify) {
        const msg = msgById.get(id);
        if (!msg) continue; // not in batch response — skip, will retry next poll

        // BUG 3 fix: skip messages that predate the baseline so rotating
        // top-25 windows on a busy inbox don't fire for days-old mail.
        // Fall through (notify) when internalDate is absent or unparseable.
        if (baselineTs !== null && msg.internalDate != null) {
          const msgTs = Number(msg.internalDate);
          if (Number.isFinite(msgTs) && msgTs < baselineTs) continue;
        }

        try {
          const from = parseAddress(getHeader(msg, 'From'));
          const sender = from.name || from.email || 'Email';
          const subject = getHeader(msg, 'Subject').trim() || '(no subject)';
          // The subject is the headline and the snippet is the body — the same
          // shape Mail uses. It previously read "New email from <sender>" with
          // the SUBJECT demoted to the body, so every row led with boilerplate
          // and the preview text was never shown at all.
          const preview = decodeSnippet(msg.snippet ?? '');
          await createNotification({
            userId,
            type: 'email_received',
            priority: 'medium',
            title: subject,
            // Fall back to naming the sender when Gmail gives no snippet
            // (calendar invites and some bare-attachment mails return "").
            message: preview || `From ${sender}`,
            actionUrl: '/email',
            relatedEntityId: msg.threadId,
            // Use the email's real receipt time so the notification shows "3 minutes
            // ago" (the email arrival) not "now" (the poll time).
            createdAt: msg.internalDate ? new Date(Number(msg.internalDate)).toISOString() : undefined,
            // sender/subject ride along so the row can regain a "from" line
            // later without another Gmail round trip — the title now carries
            // the subject, so the sender is no longer in the visible text.
            metadata: {
              source: 'gmail',
              messageId: msg.id,
              threadId: msg.threadId,
              sender,
              senderEmail: from.email || null,
              subject,
            },
          });
          // Mark notified only after successful creation.
          notified.add(id);
          raised++;
        } catch {
          // Leave this id un-notified so the next poll retries.
        }
      }

      // Persist all ids that were successfully notified in this poll.
      saveNotifiedIds(userId, notified);
    } catch {
      /* non-fatal — the unread-count poll owns the disable-on-error path */
    } finally {
      checkingRef.current = false;
    }
    return raised;
  }, [eligible, user?.id, provider]);

  // One Gmail poll for every open tab: whichever tab is visible when the slot
  // is due reads the count and checks for new mail, and the rest take its
  // answer from the broadcast.
  const pollInbox = useCallback(async (): Promise<PollResult> => {
    const share = sharesRef.current?.inbox;
    if (!eligible || !share) return 'skipped';
    if (Date.now() < disabledUntilRef.current) return false;
    const { value } = await share.run(async () => {
      const count = await readInboxCount();
      return { inboxUnread: count, raised: await checkNewEmails() };
    }, RETURN_MIN_GAP_MS);
    // An account switch replaced the share while this read was out.
    if (sharesRef.current?.inbox !== share) return 'skipped';
    const changed = inboxRef.current !== value.inboxUnread || value.raised > 0;
    applyInbox(value.inboxUnread);
    return changed;
  }, [eligible, readInboxCount, checkNewEmails, applyInbox]);

  // Gmail lifecycle is app-level, not EmailView-level: establish/renew the
  // watch and reconcile cross-device reads once per LIFECYCLE_SYNC_MS across
  // all tabs, on sign-in and then while the app stays open. The server keeps a
  // healthy watch as it is, so a call more often than this buys nothing.
  const syncLifecycle = useCallback(async (): Promise<PollResult> => {
    const share = sharesRef.current?.lifecycle;
    if (!eligible || !share) return 'skipped';
    const { value } = await share.run(async () => {
      const [watch] = await Promise.allSettled([
        MailService.startWatch(),
        MailService.reconcileNotificationReads(),
      ]);
      return watch.status === 'fulfilled' && isWatchLive(watch.value);
    }, LIFECYCLE_SYNC_MS);
    if (sharesRef.current?.lifecycle !== share) return 'skipped';
    setWatchLive(value);
    return false;
  }, [eligible]);

  useEffect(() => {
    if (!eligible || !user?.id || !providerReady) {
      inboxRef.current = null;
      setInboxUnread(0);
      setBroadcastByLabel({});
      setStarted(false);
      setChannelLive(false);
      setWatchLive(false);
      return;
    }
    disabledUntilRef.current = 0;
    const shareKey = provider === 'gmail' ? 'gmail' : 'outlook';
    const shares = {
      inbox: createTabShare<InboxPoll>(`${shareKey}-inbox:${user.id}`, { persistValue: true }),
      lifecycle: createTabShare<boolean>(`${shareKey}-lifecycle:${user.id}`, { persistValue: true }),
    };
    sharesRef.current = shares;
    const stopInbox = shares.inbox.subscribe((value) => applyInbox(value.inboxUnread));
    const stopLifecycle = shares.lifecycle.subscribe(setWatchLive);

    // EmailView broadcasts folder counts while it's open — keep them for the
    // sub-tabs. (Its INBOX value comes from the count-less list endpoint, so we
    // ignore that key and trust our own detail fetch instead.)
    const onBroadcast = (e: Event) => {
      const detail = (e as CustomEvent<{ unreadByLabel: Record<string, number> }>).detail;
      if (detail?.unreadByLabel) setBroadcastByLabel(detail.unreadByLabel);
    };
    window.addEventListener('email:label-counts', onBroadcast);

    // Pub/Sub broadcasts the authoritative Inbox count even when a change was a
    // read on another device rather than a new message.
    const gmailChannel = supabase
      .channel(`gmail:${user.id}`)
      .on('broadcast', { event: 'inbox_update' }, ({ payload }) => {
        if (!isEventForProvider(payload, provider)) return;
        if (typeof payload?.unreadCount !== 'number') return;
        applyInbox(payload.unreadCount);
        shares.inbox.publish({ inboxUnread: payload.unreadCount, raised: 0 });
      })
      .subscribe((status) => setChannelLive(status === 'SUBSCRIBED'));

    // Badge/watch reconciliation is global background work. Let the active
    // screen claim the connection pool first, then start Gmail. The Email
    // screen has its own inbox loader and broadcasts its counts back to this
    // provider, so this delay does not hold up the mailbox itself.
    const initialTimer = setTimeout(() => setStarted(true), 1_000);

    return () => {
      window.removeEventListener('email:label-counts', onBroadcast);
      clearTimeout(initialTimer);
      setStarted(false);
      stopInbox();
      stopLifecycle();
      shares.inbox.close();
      shares.lifecycle.close();
      if (sharesRef.current === shares) sharesRef.current = null;
      void supabase.removeChannel(gmailChannel);
    };
  }, [eligible, user?.id, applyInbox, providerReady, provider]);

  const pushLive = channelLive && watchLive;
  useAdaptivePoll(pollInbox, pushLive ? PUSH_POLL_POLICY : POLL_POLICY, {
    enabled: eligible && started,
    minGapMs: pushLive ? PUSH_POLL_POLICY.baseMs : RETURN_MIN_GAP_MS,
    resumeOnFocus: true,
  });
  useAdaptivePoll(syncLifecycle, LIFECYCLE_POLICY, {
    enabled: eligible && started,
    minGapMs: LIFECYCLE_SYNC_MS,
    resumeOnFocus: true,
  });

  // INBOX is always our authoritative detail count; other folders fall back to
  // the best-effort broadcast values.
  const unreadByLabel = useMemo(
    () => ({ ...broadcastByLabel, INBOX: inboxUnread }),
    [broadcastByLabel, inboxUnread],
  );

  const value = useMemo(
    () => ({ unreadByLabel, inboxUnread, refresh: fetchInbox }),
    [unreadByLabel, inboxUnread, fetchInbox],
  );

  return <EmailUnreadContext.Provider value={value}>{children}</EmailUnreadContext.Provider>;
}

const EMAIL_UNREAD_FALLBACK: EmailUnreadContextType = {
  unreadByLabel: {},
  inboxUnread: 0,
  refresh: async () => {},
};

export function useEmailUnread() {
  return useContext(EmailUnreadContext) ?? EMAIL_UNREAD_FALLBACK;
}
